import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  ExportProduct,
  ScreeningResult,
  ExportActivity,
} from 'api/ExportHubAPI';
import { JurisdictionPill, StatusPill, TierBadge } from './productPresets';

/**
 * Product 360 / History (spec §7.7) — a consolidated, time-axis narrative of
 * everything that has happened to a product: creation, screening runs, link /
 * unlink and hierarchy events, status changes, and (mocked) document packages.
 * Built from the existing product / activity / screening endpoints; document
 * attachments are mocked per §12. Reachable from global search and product detail.
 */

type Category =
  | 'created'
  | 'screening'
  | 'link'
  | 'unlink'
  | 'entity'
  | 'hierarchy'
  | 'update'
  | 'document';

interface TimelineEvent {
  key: string;
  date: string;
  category: Category;
  title: string;
  detail?: string;
  actor?: string;
}

const CAT_META: Record<Category, { icon: React.ReactNode; cls: string }> = {
  created: { icon: <AddCircleOutlineIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-created' },
  screening: { icon: <AutoAwesomeIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-screening' },
  link: { icon: <LinkOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-link' },
  unlink: { icon: <LinkOffOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-unlink' },
  entity: { icon: <PersonAddAltOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-entity' },
  hierarchy: { icon: <AccountTreeOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-hierarchy' },
  update: { icon: <EditOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-update' },
  document: { icon: <AttachFileOutlinedIcon style={{ fontSize: 16 }} />, cls: 'eh-tl-document' },
};

const categorize = (action: string): Category => {
  if (action.includes('document')) return 'document';
  if (action.includes('unlink')) return 'unlink';
  if (action.includes('entity')) return 'entity';
  if (action.includes('link')) return 'link';
  if (action.includes('child') || action.includes('bom')) return 'hierarchy';
  return 'update';
};

const Product360: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;

  const [product, setProduct] = useState<ExportProduct | null>(null);
  const [screenings, setScreenings] = useState<ScreeningResult[]>([]);
  const [activity, setActivity] = useState<ExportActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!productId || !groupId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const [p, s, a] = await Promise.all([
        ExportHubAPI.getProduct(productId, groupId),
        ExportHubAPI.getScreenings(productId, groupId),
        ExportHubAPI.getProductActivity(productId, groupId),
      ]);
      if (p) setProduct(p);
      else setNotFound(true);
      setScreenings(s);
      setActivity(a);
    } catch (err) {
      console.error('Failed to load product history:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [productId, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className='export-hub-panel'>
        <div className='eh-table-skeleton'>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <>
        <Link to='/export-hub/products' className='detail-back-link'>
          <ArrowBackIcon style={{ fontSize: 14 }} /> Back to Products Overview
        </Link>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>Product not found</div>
          <div className='export-hub-empty-sub'>
            This product may have been deleted, or it belongs to another team.
          </div>
        </div>
      </>
    );
  }

  const detailHref = `/export-hub/databases/${product.database_id}/products/${product.id}`;

  const createdEvent: TimelineEvent = {
    key: 'created',
    date: product.created_at,
    category: 'created',
    title: 'Product created',
    detail: `Added to the database as a ${product.tier}`,
    actor: product.created_by_name || 'Someone',
  };
  const events: TimelineEvent[] = [
    createdEvent,
    ...activity.map(
      (a): TimelineEvent => ({
        key: a.id,
        date: a.created_at,
        category: categorize(a.action),
        title: a.message,
        actor: a.actor_name || 'Someone',
      }),
    ),
    ...screenings.map(
      (s): TimelineEvent => ({
        key: s.id,
        date: s.created_at,
        category: 'screening',
        title: `Screening run — ${s.check_type.replace(/_/g, ' ')}`,
        detail: s.risk ? `Risk: ${s.risk}` : undefined,
        actor: s.created_by_name || 'Someone',
      }),
    ),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Group events by calendar day for the time-axis rail.
  const groups: Array<{ day: string; items: TimelineEvent[] }> = [];
  events.forEach((e) => {
    const day = moment(e.date).format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(e);
    else groups.push({ day: day, items: [e] });
  });

  return (
    <>
      <div className='export-hub-breadcrumb'>
        <Link to='/export-hub/products' className='export-hub-crumb-link'>
          <HomeOutlinedIcon style={{ fontSize: 16, verticalAlign: 'text-bottom', marginRight: 4 }} />
          Export Hub
        </Link>
        <span className='export-hub-crumb-sep'>
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </span>
        <Link to={detailHref} className='export-hub-crumb-link'>
          {product.name}
        </Link>
        <span className='export-hub-crumb-sep'>
          <ChevronRightIcon style={{ fontSize: 16 }} />
        </span>
        <span className='export-hub-crumb-current'>History</span>
      </div>

      <div className='export-hub-header'>
        <div>
          <h1 className='export-hub-title'>
            <TimelineOutlinedIcon
              style={{ fontSize: 24, marginRight: 8, verticalAlign: 'middle' }}
            />
            {product.name}
          </h1>
          <div className='eh-detail-badges'>
            <span className='eh-detail-sku'>
              {product.internal_sku ? `SKU ${product.internal_sku}` : 'No SKU'}
            </span>
            <TierBadge value={product.tier} />
            <JurisdictionPill value={product.jurisdiction} />
            <StatusPill value={product.compliance_status} />
            <span className='eh-detail-meta-text'>
              Everything that has ever happened to this product
            </span>
          </div>
        </div>
        <div className='export-hub-header-actions'>
          <Link to={detailHref} className='btn-outline'>
            Open product
          </Link>
        </div>
      </div>

      <div className='export-hub-panel'>
        <div className='eh-timeline'>
          {groups.map((g) => (
            <div className='eh-tl-group' key={g.day}>
              <div className='eh-tl-day'>{moment(g.day).format('MMMM D, YYYY')}</div>
              {g.items.map((e) => (
                <div className='eh-tl-row' key={e.key}>
                  <span className={`eh-tl-icon ${CAT_META[e.category].cls}`}>
                    {CAT_META[e.category].icon}
                  </span>
                  <div className='eh-tl-content'>
                    <div className='eh-tl-title'>{e.title}</div>
                    {e.detail && <div className='eh-tl-detail'>{e.detail}</div>}
                    <div className='eh-tl-meta'>
                      {e.actor} · {moment(e.date).format('h:mm A')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Product360;
